import type { TelemetryEvent } from "./telemetry";

// Traffic belongs to the public site. Admin page activity is audited separately and must not
// inflate the public site's PV/UV figures.
const ADMIN_PATH_PREFIXES = ["/admin", "/login", "/providers", "/api-clients", "/query-logs", "/languages"];

export function createPageViewEvent(pathname: string, documentLocale?: string, referrer?: string): TelemetryEvent | null {
  const normalizedPath = normalizePath(pathname);
  if (ADMIN_PATH_PREFIXES.some((prefix) => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`))) {
    return null;
  }

  return {
    eventType: "PageView",
    pagePath: normalizedPath,
    locale: localeFromPath(normalizedPath) ?? normalizeLocale(documentLocale),
    referer: referrer || undefined,
  };
}

/**
 * The referrer to report for a page view: where the visitor was immediately before THIS page.
 *
 * `document.referrer` is fixed for the life of a document — a client-side route change does not update
 * it — so reporting it on every route change would repeat the original external referrer (say, Google)
 * on pages the visitor reached by clicking around the site, and would never show the in-site hop at all.
 * The first view of a document uses the real `document.referrer`; every later one uses the previous
 * in-site page (origin + path; the query string is dropped, it can hold an address the visitor looked up).
 *
 * This is the CURRENT referrer only. It says nothing about how the visitor found the site — that is the
 * session's first touch (trafficAttribution.ts).
 */
export function referrerForPageView(previousPageUrl: string | null, documentReferrer: string): string {
  return previousPageUrl ?? documentReferrer;
}

function normalizePath(pathname: string): string {
  const path = pathname.trim();
  if (!path || path === "/") return "/";
  return `/${path.replace(/^\/+|\/+$/g, "")}`;
}

function localeFromPath(pathname: string): string | undefined {
  const firstSegment = pathname.split("/")[1];
  return normalizeLocale(firstSegment);
}

function normalizeLocale(locale?: string): string | undefined {
  const normalized = locale?.trim().toLowerCase();
  return normalized && /^[a-z]{2}(?:-[a-z]{2})?$/.test(normalized) ? normalized : undefined;
}
